import React, { useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { useRegionCookieConsentContext } from 'contexts/cookie-consent'
import DiscoModal from 'components/ui/disco-modal'
import Heading from 'components/ui/heading'
import modalStyles from 'components/utils/modalStyles'
// don't change relative imports due resolution conflict on website
import Button from '../../ui/button'
import Switch from '../../ui/switch'
import type { CSSProperties } from 'react'
import styles from './index.module.css'

type Props = {
  cookieLink: string
  privacyPoliciesLink: string
  onClose: (...args: Array<any>) => any
  onSubmit: (...args: Array<any>) => any
}

const CookiePreferences = ({
  cookieLink,
  privacyPoliciesLink,
  onClose,
  onSubmit,
}: Props): React.ReactNode => {
  const { consent } = useRegionCookieConsentContext()
  // if cookie is present (consent.necessary === true) then use value from cookie
  // otherwise use default value that enables statistics
  const notStrictlyNecessaryDefault = consent.necessary
    ? consent.statistics
    : true
  const [toggleNotStrictly, setToggleNotStrictly] = useState(
    notStrictlyNecessaryDefault,
  )
  const isMobile = useMediaQuery({
    maxWidth: 600,
  })
  let style: Record<string, CSSProperties> = {
    overlay: {
      ...modalStyles.overlay,
      placeItems: 'stretch',
      placeContent: 'flex-end',
      padding: 0,
      zIndex: 300,
    },
    content: {
      ...modalStyles.content,
      margin: 0,
      borderRadius: 0,
      padding: 0,
      display: 'flex',
      placeItems: 'flex-start',
      flexDirection: 'column',
    },
  }

  if (isMobile) {
    style.overlay.placeContent = 'center'
    style.content.width = '100%'
  }

  return (
    <DiscoModal onRequestClose={onClose} style={style}>
      <div className={styles.cookiePreferences}>
        <div className={styles.cookiePreferencesTextContent}>
          <Heading size={800}>DISCO Cookie Preferences</Heading>

          <p className={styles.cookiePreferencesText}>
            DISCO uses cookies to improve service, performance, and to better
            understand your needs.
          </p>
          <p className={styles.cookiePreferencesText}>
            Here you can learn more about the cookies DISCO uses and manage your
            cookie preferences.
          </p>
          <p className={styles.cookiePreferencesText}>
            Please also refer to our{' '}
            <a
              href={cookieLink}
              className="text-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Cookie
            </a>{' '}
            and{' '}
            <a
              href={privacyPoliciesLink}
              className="text-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policies
            </a>{' '}
            for additional information.
          </p>
          <div className={styles.cookiePreferencesLine} />
          <div className={styles.cookiePreferencesSwitchContainer}>
            <Switch onChange={() => {}} value disabled />
            <div className={styles.cookiePreferencesSwitchHeader}>
              <Heading size={600}>Strictly necessary cookies</Heading>
            </div>
          </div>

          <p className={styles.cookiePreferencesText}>
            We define strictly necessary cookies as cookies that are used to
            operate the DISCO and its core features, for example to log you in
            and keep your account secure. They are required to provide our
            services to you and cannot be disabled.
          </p>

          <div className={styles.cookiePreferencesSwitchContainer}>
            <Switch
              value={toggleNotStrictly}
              onChange={() => setToggleNotStrictly(!toggleNotStrictly)}
            />
            <div className={styles.cookiePreferencesSwitchHeader}>
              <Heading size={600}>Not strictly necessary cookies</Heading>
            </div>
          </div>

          <p className={styles.cookiePreferencesText}>
            We define not strictly necessary cookies as various other types of
            cookies which help with analytics and other parts of the service.
            Among other things, these cookies help us and our users understand
            how visitors interact with inboxes, track pages, playlists and
            Catalogs and are used to remember certain settings, measure traffic
            and provide reliable and personalized support. While we recommend
            you keep these enabled, they are not strictly required and can be
            disabled.
          </p>
        </div>

        <div className={styles.cookiePreferencesButtonContainer}>
          <Button caption="Cancel" onClick={onClose} />
          <Button
            caption="Confirm Preferences"
            appearance="primary"
            onClick={() =>
              onSubmit({
                statistics: toggleNotStrictly,
              })
            }
          />
        </div>
      </div>
    </DiscoModal>
  )
}

export default CookiePreferences
